import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Phone number + OTP verification. Controlled: parent owns `phone`.
// Calls onVerified(true/false) as the verified state changes.
export default function PhoneVerify({ phone, onPhoneChange, onVerified, placeholder = "10-digit mobile", required }) {
  const [sent, setSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);

  const digits = (phone || "").replace(/\D/g, "");
  const canSend = digits.length >= 10 && !busy;

  const editPhone = (v) => {
    onPhoneChange(v);
    if (verified) { setVerified(false); onVerified?.(false); }
    if (sent) { setSent(false); setOtp(""); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/send", { phone, purpose: "verify" });
      setSent(true);
      toast.success(data.mock
        ? "Code sent (test mode — check the server logs for the OTP)"
        : "OTP sent to your mobile number");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the code");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/otp/verify", { phone, otp, purpose: "verify" });
      setVerified(true); onVerified?.(true);
      toast.success("Mobile number verified");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex gap-2 items-center">
        <input
          value={phone}
          onChange={(e) => editPhone(e.target.value)}
          disabled={verified}
          inputMode="numeric"
          placeholder={placeholder}
          required={required}
          data-testid="phone-input"
          className="underline-input flex-1 disabled:opacity-70"
        />
        {verified ? (
          <span data-testid="phone-verified" className="text-[hsl(var(--accent))] font-mono text-[10px] uppercase tracking-widest whitespace-nowrap">
            ✓ Verified
          </span>
        ) : (
          <button
            type="button" onClick={send} disabled={!canSend}
            data-testid="send-otp-btn"
            className="border border-foreground px-3 py-2 text-[10px] font-mono uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {sent ? "Resend" : "Send OTP"}
          </button>
        )}
      </div>

      {sent && !verified && (
        <div className="flex gap-2 items-center mt-3">
          <input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="Enter 6-digit OTP"
            data-testid="otp-input"
            className="underline-input flex-1"
          />
          <button
            type="button" onClick={verify} disabled={busy || otp.length < 6}
            data-testid="verify-otp-btn"
            className="bg-foreground text-background px-3 py-2 text-[10px] font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-40 whitespace-nowrap"
          >
            Verify
          </button>
        </div>
      )}
    </div>
  );
}
